import { ISearchParams, ISearchResult } from 'api/common.types'
import { IReconData } from 'api/recondata.types'
import { search } from 'api/search'
import axios from 'axios'
import { IApiOptions } from 'shared/contracts/IApiOptions'
import { getRockefellerApiOptions } from 'store/shared'
import { call, cancelled, delay, put, takeLatest } from 'typed-redux-saga'
import {
  fetchBreakDetailAttrService,
  fetchReconDetailsService,
  fetchResolvedCountService,
  postReconAddCommentsService,
  postSEIReconAddCommentsService
} from '../api/reconDataService'
import {
  addCommentsReconActions,
  fetchBreakDetailAttrActions,
  fetchReconDetailsActions,
  fetchResolvedCountActions,
  reconSummarySearchActions
} from './actions'

function* handleReconDetailsActions(
  action: ReturnType<typeof fetchReconDetailsActions.request>
) {
  // eslint-disable-next-line import/no-named-as-default-member
  const source = axios.CancelToken.source()
  const apiOptions = yield* call(getRockefellerApiOptions, source.token)

  try {
    const result = yield* call(
      fetchReconDetailsService,
      action.payload,
      apiOptions
    )
    yield put(fetchReconDetailsActions.success(result))
  } catch (e: any) {
    yield put(fetchReconDetailsActions.failure(e))
  } finally {
    if (yield* cancelled()) {
      source.cancel()
    }
  }
}

function* handleReconAddComments(
  action: ReturnType<typeof addCommentsReconActions.request>
) {
  try {
    const options = yield* call(getRockefellerApiOptions)
    const { payload } = action
    const service = payload?.isSeiRequest
      ? postSEIReconAddCommentsService
      : postReconAddCommentsService
    const apiResp = yield* call(() => service(payload, options))
    if (apiResp) {
      yield put(addCommentsReconActions.success(apiResp))
    } else {
      yield put(
        addCommentsReconActions.failure(
          new Error(apiResp || 'Operation failed, please try after sometime.')
        )
      )
    }
  } catch (e: any) {
    yield put(
      addCommentsReconActions.failure(
        new Error('Operation failed, please try after sometime.')
      )
    )
  }
}

function* handleReconSummarySearch() {
  try {
    yield delay(300)
    const options: IApiOptions = yield call(getRockefellerApiOptions)
    const params: ISearchParams = {
      orderBy: [
        { dataPath: 'search.score()', direction: 'desc' },
        { dataPath: 'id', direction: 'desc' }
      ]
    }

    const response: ISearchResult<IReconData> = yield call(
      search,
      'dqrbreaksummary' as const,
      {
        ...params,
        select: [
          'accountId',
          'accountId',
          'legalEntityName',
          'accountNo',
          'securityDesc',
          'cusip',
          'breakSummaryDetails',
          'breakDesc',
          'breakdetailcount',
          'drawdown',
          'assignedTo',
          'committedCapital',
          'capitalCalled',
          'remainingCapital',
          'distributionIncome',
          'breakSummaryDetails',
          'breakSummaryStatus',
          'platform'
        ],
        facets: ['securityDesc', 'breakSummaryStatus', 'assignedTo'],
        top: 0
      },
      options
    )

    if (response && response?.value) {
      yield put(reconSummarySearchActions.success(response))
    } else {
      yield put(
        reconSummarySearchActions.failure(
          new Error('unable to fetch Recon Summary list')
        )
      )
    }
  } catch (e: any) {
    yield put(reconSummarySearchActions.failure(e))
  }
}

function* handleFetchBreakDetailAttre() {
  try {
    yield delay(300)
    const options: IApiOptions = yield call(getRockefellerApiOptions)
    const response = yield* call(() => fetchBreakDetailAttrService(options))
    if (response.success) {
      yield put(fetchBreakDetailAttrActions.success(response))
    } else {
      yield put(
        fetchBreakDetailAttrActions.failure(
          new Error('Unable to fetch Cash Raise dashboard list')
        )
      )
    }
  } catch (e: any) {
    yield put(fetchBreakDetailAttrActions.failure(e))
  }
}

function* handleFetchResolvedCount() {
  try {
    const options: IApiOptions = yield call(getRockefellerApiOptions)
    const count = yield* call(() => fetchResolvedCountService(options))
    yield put(fetchResolvedCountActions.success(count))
  } catch (e: any) {
    yield put(fetchResolvedCountActions.failure(e))
  }
}

export const reconDataSummarySagas = [
  () => takeLatest(fetchReconDetailsActions.request, handleReconDetailsActions),
  () => takeLatest(addCommentsReconActions.request, handleReconAddComments),
  () =>
    takeLatest(
      fetchBreakDetailAttrActions.request,
      handleFetchBreakDetailAttre
    ),
  () => takeLatest(fetchResolvedCountActions.request, handleFetchResolvedCount),
  () => takeLatest(reconSummarySearchActions.request, handleReconSummarySearch)
]
